import React, { useState } from 'react';
import { Link } from 'react-router-dom';

const DuplicateWarning = ({ duplicates, onConfirm, onCancel, isVisible }) => {
  const [showDetails, setShowDetails] = useState(false);

  if (!isVisible || !duplicates || duplicates.length === 0) {
    return null;
  }

  const getColorByScore = (score) => {
    if (score >= 90) return 'bg-red-100 text-red-800 border-red-300';
    if (score >= 80) return 'bg-orange-100 text-orange-800 border-orange-300';
    return 'bg-yellow-100 text-yellow-800 border-yellow-300';
  };

  const getIconByScore = (score) => {
    if (score >= 90) return '🚨';
    if (score >= 80) return '⚠️';
    return '💡';
  };

  const highestScore = Math.max(...duplicates.map(d => d.similarity_score));

  return (
    <div className="modal-overlay">
      <div className="modal-content max-w-4xl">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center mb-6">
            <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mr-4">
              <span className="text-2xl">{getIconByScore(highestScore)}</span>
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">
                Обнаружены похожие проекты
              </h2>
              <p className="text-gray-600">
                Найдено {duplicates.length} потенциальных дублей с похожестью до {highestScore}%
              </p>
            </div>
          </div>

          {/* Warning Message */}
          <div className={`p-4 rounded-lg border-l-4 mb-6 ${getColorByScore(highestScore)}`}>
            <div className="flex">
              <div className="ml-3">
                <h3 className="text-sm font-medium">
                  {highestScore >= 90 ? 'Очень высокая похожесть' : 
                   highestScore >= 80 ? 'Высокая похожесть' : 'Средняя похожесть'}
                </h3>
                <p className="mt-1 text-sm">
                  {highestScore >= 90 ? 
                    'Этот проект может быть точной копией существующего. Рекомендуем тщательно проверить перед добавлением.' :
                    'Обнаружены проекты с похожими характеристиками. Проверьте, не является ли это дублированием.'
                  }
                </p>
              </div>
            </div>
          </div>

          {/* Duplicates List */}
          <div className="mb-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">Похожие проекты</h3>
              <button
                onClick={() => setShowDetails(!showDetails)}
                className="text-sm text-blue-600 hover:text-blue-800"
              >
                {showDetails ? 'Скрыть детали' : 'Показать детали'}
              </button>
            </div>

            <div className="space-y-3">
              {duplicates.map((duplicate, index) => (
                <div
                  key={duplicate.project.id}
                  className="border rounded-lg p-4 bg-gray-50 hover:bg-gray-100 transition-colors"
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h4 className="font-medium text-gray-900">
                          <Link 
                            to={`/projects/${duplicate.project.id}`}
                            className="hover:text-blue-600 transition-colors"
                            target="_blank"
                          >
                            {duplicate.project.name}
                          </Link>
                        </h4>
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${getColorByScore(duplicate.similarity_score)}`}>
                          {duplicate.similarity_score}% похожесть
                        </span>
                      </div>
                      
                      <div className="text-sm text-gray-600 grid grid-cols-2 gap-4">
                        <div>
                          <span className="font-medium">Компания:</span> {duplicate.project.customer_company || 'Не указана'}
                        </div>
                        <div>
                          <span className="font-medium">Город:</span> {duplicate.project.city}
                        </div>
                        {duplicate.project.budget_amount && (
                          <div>
                            <span className="font-medium">Бюджет:</span> {new Intl.NumberFormat('ru-RU').format(duplicate.project.budget_amount)} {duplicate.project.budget_currency}
                          </div>
                        )}
                        <div>
                          <span className="font-medium">Этап:</span> {duplicate.project.stage}
                        </div>
                      </div>

                      {showDetails && duplicate.project.notes && (
                        <div className="mt-3 p-3 bg-white rounded border">
                          <span className="font-medium text-sm">Комментарии:</span>
                          <p className="text-sm text-gray-600 mt-1">{duplicate.project.notes}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4 border-t">
            <button
              onClick={onCancel}
              className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
            >
              Отменить создание
            </button>
            <button
              onClick={onConfirm}
              className="flex-1 px-4 py-2 bg-blue-600 text-white hover:bg-blue-700 rounded-lg transition-colors"
            >
              Все равно создать проект
            </button>
          </div>

          <p className="text-xs text-gray-500 mt-3 text-center">
            💡 Совет: Проверьте существующие проекты перед созданием нового
          </p>
        </div>
      </div>
    </div>
  );
};

export default DuplicateWarning;